import { SERVER_URL, WORKER_URL, catalogJsonUrl } from '../js/config.js';

export class ApiClient {
  constructor(options = {}) {
    this.baseUrl = options.baseUrl || this._getBaseUrl();
    this.tokenKey = options.tokenKey || 'auth_token';
    this.onUnauthorized = options.onUnauthorized || this._defaultUnauthorizedHandler;
  }

  _getBaseUrl() {
    return typeof SERVER_URL !== 'undefined' ? SERVER_URL :
           window.config?.SERVER_URL || window.SERVER_URL || window.location.origin;
  }

  _defaultUnauthorizedHandler() {
    this.clearToken();
    window.showLoginModal?.();
  }

  async get(url, options = {}) {
    return this.request(url, { ...options, method: 'GET' });
  }

  async post(url, data = {}, options = {}) {
    return this.request(url, { ...options, method: 'POST', data });
  }

  async put(url, data = {}, options = {}) {
    return this.request(url, { ...options, method: 'PUT', data });
  }

  async delete(url, options = {}) {
    return this.request(url, { ...options, method: 'DELETE' });
  }

  async request(url, options = {}) {
    const requestUrl = url.startsWith('http') ? url : `${this.baseUrl}${url}`;
    const isLocalDev = ['localhost', '127.0.0.1'].includes(window.location.hostname);
    
    if (url.includes('/api/v1/faq')) {
      throw new Error(`Endpoint ${url} not available`);
    }

    const cdnMappings = {
      '/api/v1/characters/rankings': 'data/characters/characters_rankings.json'
    };

    let finalUrl = requestUrl;
    if (isLocalDev) {
      const mappingKey = Object.keys(cdnMappings).find(key => url.includes(key));
      if (mappingKey) {
        finalUrl = catalogJsonUrl(cdnMappings[mappingKey]);
      }
    }

    const { method = 'GET', data, params, headers = {} } = options;
    
    if (params) {
      const query = Object.entries(params)
        .filter(([, v]) => v != null)
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join('&');
      if (query) {
        finalUrl += (finalUrl.includes('?') ? '&' : '?') + query;
      }
    }

    const hasTokenManager = !!(window.tokenManager && typeof window.tokenManager.getValidAccessToken === 'function');

    // Always try to resolve a valid token. This is critical for cross-origin Workers calls
    // (cookies won't be sent to *.workers.dev, so Authorization must be present).
    let token = this.getToken();
    if (hasTokenManager) {
      try {
        token = await window.tokenManager.getValidAccessToken();
      } catch {
        // ignore and fall back to whatever we already had
      }
    }

    // For Worker URLs that require authentication, validate token exists
    const requiresAuth = finalUrl.includes('.workers.dev') || finalUrl.includes('/create-') || 
                         finalUrl.includes('/recruit/') || finalUrl.includes('/draft/');
    
    if (requiresAuth && !token && method !== 'OPTIONS') {
      console.warn(`[ApiClient] No auth token for protected endpoint: ${method} ${finalUrl}`);
      this.onUnauthorized();
      throw new Error('Authentication required');
    }

    const requestHeaders = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers
    };

    const requestOptions = {
      method,
      headers: requestHeaders,
      credentials: finalUrl.includes(WORKER_URL) ? 'same-origin' : 'include',
      ...(data && ['POST', 'PUT', 'PATCH'].includes(method) && { body: JSON.stringify(data) })
    };

    try {
      const response = await fetch(finalUrl, requestOptions);

      const contentType = response.headers.get('content-type');
      const responseData = contentType?.includes('application/json') 
        ? await response.json() 
        : await response.text();

      if (!response.ok) {
        // One retry path: token could have expired between click and request; refresh and retry once.
        if (response.status === 401 && hasTokenManager && !options._isRetry) {
          try {
            const refreshed = await window.tokenManager.refreshAccessToken?.();
            if (refreshed) {
              return this.request(url, {
                ...options,
                _isRetry: true,
                headers: { ...headers, Authorization: `Bearer ${refreshed}` }
              });
            }
          } catch {
            // fall through to unauthorized handling
          }
        }
        if (responseData.code === 'AUTH_REQUIRED' || response.status === 401) {
          this.onUnauthorized();
          throw new Error('Authentication required');
        }
        const error = new Error(responseData.message || responseData.error || 'API request failed');
        Object.assign(error, { status: response.status, code: responseData.code, data: responseData });
        throw error;
      }

      return responseData;
    } catch (error) {
      console.error(`API request failed: ${method} ${finalUrl}`, error);
      throw error;
    }
  }

  isAuthenticated() {
    return !!this.getToken();
  }

  getToken() {
    // Prefer TokenManager as the source of truth (it also supports refresh via HttpOnly cookies).
    try {
      const tmToken = window.tokenManager?.getAccessToken?.();
      if (tmToken) return tmToken;
    } catch {}

    // Legacy fallback
    try {
      return sessionStorage.getItem(this.tokenKey);
    } catch {
      return null;
    }
  }

  setToken(token) {
    try { sessionStorage.setItem(this.tokenKey, token); } catch {}
  }

  clearToken() {
    try { sessionStorage.removeItem(this.tokenKey); } catch {}

  }
}

const apiClient = new ApiClient();
export default apiClient;
